import * as XLSX from 'xlsx' // xlsxライブラリをインポート
import { ModelFactory } from '../../common/$models'
import { ColumnDefByColName } from '../../types'

const ignoreFromExportColumnTypes = [
  'ARRAY_OF_OBJECT', // flatternされるので無視
]
const skipExportColumnNames = ['objectID'] // , 'createdAt', 'updatedAt', '作成日時', '更新日時'

// nestedCol.0.name のようなpathのlabelを取得する
export const getColumnLabelByDottedNestedPath = (
  dottedColName: string,
  colDefs: ColumnDefByColName,
  res = '',
) => {
  const paths = dottedColName.split('.')
  for (let i = 0; paths.length > i; i++) {
    const colName = paths[i]
    const col = i === 0 ? colDefs[colName] : colDefs[paths[0]]?.columns?.[colName] || null
    const label = col?.label || colName
    if (label) {
      res += (res ? '.' : '') + label
    }
  }
  return res
}

const getFallbackExportFilename = () =>
  `エクスポート_${$core.$dayjs().format('YYYY-MM-DD_HHmmss')}`

/**
 * Excel のシート名に使用できない文字, length を 修正する
 * @param sheetName
 * @returns
 */
const modifySheetName = (sheetName: string) => {
  let modified = sheetName
    // 禁止文字の除去 (\ / : * ? " < > | [ ] ( ) ' などの特殊文字)
    // eslint-disable-next-line
    .replace(/[\\/:*?"<>|\[\]()'\u0000-\u001F\u007F]/g, '')
    // 連続するスペースを単一スペースに変換
    .replace(/\s+/g, '_')
    // 全角スペースをアンダースコアに置換
    // eslint-disable-next-line
    .replace(/　/g, '_')
    // 連続するアンダースコアを単一のアンダースコアに置換
    .replace(/_+/g, '_')

  // 空文字列になった場合のデフォルト値設定
  if (!modified) {
    modified = 'Sheet'
  }

  // 文字数制限（31文字）
  return modified.slice(0, 31)
}

/**
 * # $core.$exportAsExcel
 * - `ARRAY_OF_OBJECT` と `Table` からExcelデーターを出力するオブジェクトリテラル
 */
export const $exportAsExcel = {
  /**
   * SheetJS library https://docs.sheetjs.com/docs/
   */
  get XLSX(): typeof XLSX {
    return XLSX
  },
  /**
   * `ARRAY_OF_OBJECT` から `Excel`ファイルを生成するための非同期メソッドです。
   *
   * 様々なオプションを提供してカスタマイズ可能です。
   * @param opts オプション
   * @param opts.data Excelファイルにエクスポートしたいデータを含むオブジェクトの配列 (必須)
   * @param opts.sheetName シート名 (default: Sheet1)
   * @param opts.filename ファイル名 (default: exported)
   * @param opts.headerRow ヘッダー行 (default: null)
   * @param opts.modelName モデル名 (モデル名を指定すると、モデルのカラム定義を参照してヘッダー行を整形します)
   * @param opts.flatternNestedData ネストされたデータを展開するかどうか (default: true)
   * @param opts.returnWorkSheetObject ワークシートオブジェクトを返すかどうか (default: false)
   * @param opts.exportFormatType 出力形式 (xlsx または csv) (default: xlsx)
   */
  fromArrayOfObject: async ({
    data,
    sheetName = 'Sheet1',
    filename = 'exported',
    headerRow = null,
    modelName = null,
    flatternNestedData = true,
    returnWorkSheetObject = false,
    exportFormatType = 'xlsx',
  }: {
    data: Record<string, any>[]
    sheetName?: string
    filename?: string
    headerRow?: string[] | null
    modelName?: string | null
    flatternNestedData?: boolean
    returnWorkSheetObject?: boolean
    exportFormatType?: 'xlsx' | 'csv'
  }) => {
    const headerRowsAsObj = (headerRow || []).reduce((res, r) => {
      res[r] = true
      return res
    }, {})
    try {
      if (flatternNestedData) {
        data = data.map((d) => {
          const flatted = $core.$utils.flattenObject(d)
          Object.keys(flatted).map((key) => {
            if (skipExportColumnNames.indexOf(key) >= 0) {
              delete flatted[key]
            }
            headerRowsAsObj[key] = true
          })
          return flatted
        })
      }
      headerRow = Object.keys(headerRowsAsObj)
      if (modelName && globalThis.$core.$models[modelName]) {
        // @ts-ignore
        const model = globalThis.$core.$models[modelName] as ModelFactory
        // headerRow 並び替え: Alphabet順で整形した上で、colNames順にする
        headerRow = headerRow
          .filter(
            (colName) =>
              skipExportColumnNames.indexOf(colName) === -1 &&
              ignoreFromExportColumnTypes.indexOf(model.columns[colName]?.type) === -1,
          )
          .sort((a, b) => a.localeCompare(b)) // Alphabet順

        if (model) {
          // TODO: Too dirty...
          headerRow = headerRow.sort((a, b) => {
            if (a === 'id') {
              return -1
            }
            if (b === 'id') {
              return 1
            }
            const indA = model.colNames.indexOf(a)
            const indB = model.colNames.indexOf(b)
            if (indA === -1 && indB === -1) {
              return 0
            }
            if (indA === -1 && indB !== -1) {
              return 1
            }
            if (indA !== -1 && indB === -1) {
              return -1
            }
            return indA - indB > 0 ? 1 : -1
          })
        }
        // 1行目にカラム定義のlabel を利用する
        const labelRow = headerRow.reduce((res, colName) => {
          if (model.colLabels[colName]) {
            res[colName] = model.colLabels[colName]
          } else if (colName.indexOf('.') >= 0) {
            res[colName] = getColumnLabelByDottedNestedPath(colName, model.columns)
          }
          return res
        }, {})
        data = [labelRow, ...data]
      }
      const ws = XLSX.utils.json_to_sheet(data, {
        header: headerRow,
        skipHeader: false,
      })
      // ワークシートオブジェクトのみ返却パターン
      if (returnWorkSheetObject) {
        return ws
      }
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, modifySheetName(sheetName))
      XLSX.writeFile(wb, `${filename || getFallbackExportFilename()}.${exportFormatType}`)
    } catch (e) {
      console.error(e)
      if (e.message.indexOf('Text length must not exceed') >= 0) {
        $core.$toast.errorToast(
          'エクスポートするデータが大きすぎます。1セルに入力できる 最大文字数 (32,767文字) を超えています。',
        )
      } else {
        $core.$toast.errorToast(`エクスポートに失敗しました。 (Error: ${e.message})`)
      }
      throw e
    }
  },

  /**
   * テーブル要素からExcelファイルを生成してダウンロードします。
   *
   * @param args
   * @param args.id テーブル要素のID (default: null)
   * @param args.filename ファイル名 (default: null)
   * @param args.element テーブル要素 (default: null)
   * @param args.cellStyles セルスタイルを有効にするかどうか (default: false)
   * @returns
   */
  async fromTableElement({
    id = null,
    filename,
    element = null,
    cellStyles = false,
    format = 'xlsx',
  }) {
    if (!element) {
      element = window.document.getElementById(id)
    }
    if (!element) {
      globalThis.$core.$toast.errorToast(`Table要素が見つかりませんでした。 id: ${id}`)
      return
    }
    // TODO: cellStyles, 各tablecellに対しては、computed style (props) を参照すればより良くなるはずだ
    XLSX.writeFile(
      XLSX.utils.table_to_book(element, {
        display: true,
      }),
      `${filename || id || getFallbackExportFilename()}.${format}`,
      { cellStyles },
    )
  },
  /**
   * Excel のシート名に使用できない文字, length を 修正する
   * @param sheetName
   * @returns
   */
  validateAndNormalizeSheet2JsonOptions,
  /**
   * Excel のシートを JSON に変換する関数 (Sheetjs の sheet_to_json のエイリアスであるが オプションの 検証・正規化を実施している)
   * @param worksheet Excel シート
   * @param opts オプション (省略可能)
   * @returns JSON データ
   */
  sheetToJson(worksheet: XLSX.WorkSheet, opts?: Sheet2JSONOpts) {
    const normalizedOpts = validateAndNormalizeSheet2JsonOptions(opts)
    return XLSX.utils.sheet_to_json(worksheet, normalizedOpts)
  },
  /**
   * Sheetjs の Excel 読み取り範囲指定を検証する正規表現
   * - 例: "A1:C5", "B6:AD99999", 6 (開始行指定)
   * @param val 検証する値
   * @returns boolean false の場合 無効な範囲指定
   */
  validateExcelRangeValue,
}

// sheet_to_json のオプションの型定義 (xlsxライブラリの Sheet2JSONOpts を利用)
type Sheet2JSONOpts = XLSX.Sheet2JSONOpts

// デフォルト値を定義
const defaultOptions: Required<Omit<Sheet2JSONOpts, 'range' | 'defval' | 'rawNumbers'>> &
  Pick<Sheet2JSONOpts, 'range' | 'defval' | 'rawNumbers'> = {
  header: undefined,
  // range はデフォルトでシート全体。指定がない場合は undefined のままにする
  range: undefined,
  blankrows: true,
  // defval はデフォルトで undefined
  defval: undefined,
  raw: true,
  // rawNumbers は指定がなければ raw の値に依存
  rawNumbers: undefined, // 初期値は undefined とし、後で raw の値に基づいて設定
  dateNF: 'yyyy-mm-dd', // DateNFOption からの継承 (例として)
  // sheetStubs, cellDates など、DateNFOption に含まれる他のオプションも必要に応じて追加
}

function validateExcelRangeValue(val: string | number): boolean {
  if (val === undefined) {
    return false
  }
  // 数値のみの場合（開始行指定）
  if (typeof val === 'number') {
    return true
  }
  if (/^\d+$/.test(`${val}`)) {
    return true // OK
  }

  // 範囲指定値 (例: B6:AD99999)
  const excelRangeRegex = /^([A-Z]+)([0-9]+):([A-Z]+)([0-9]+)$/
  const match = val.match(excelRangeRegex)

  if (!match) {
    return false
  }

  // 範囲の妥当性チェック
  const [, startCol, startRow, endCol, endRow] = match

  // 列文字をインデックスに変換する関数
  const colToIndex = (col) => {
    let index = 0
    for (let i = 0; i < col.length; i++) {
      index = index * 26 + col.charCodeAt(i) - 64 // A=65 なので64を引く
    }
    return index
  }

  const startColIndex = colToIndex(startCol)
  const endColIndex = colToIndex(endCol)
  const startRowNum = parseInt(startRow, 10)
  const endRowNum = parseInt(endRow, 10)

  // 範囲チェック（始点が終点より大きい場合はエラー）
  if (startColIndex > endColIndex) {
    return false
  }

  if (startRowNum > endRowNum) {
    return false
  }
  return true
}
/**
 * Excel の A1 形式の範囲指定文字列を検証する正規表現
 * - セル範囲 (例: "A1:C5")
 */
const excelCellRangeRegex = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/

/**
 * sheet_to_json のオプションを検証し、デフォルト値で正規化する関数
 *
 * @param options ユーザーが指定したオプション (省略可能)
 * @returns 検証・正規化されたオプション
 */
export function validateAndNormalizeSheet2JsonOptions(
  options?: Partial<Sheet2JSONOpts>,
): Sheet2JSONOpts {
  // デフォルト値で初期化
  const normalizedOptions: Sheet2JSONOpts = { ...defaultOptions }

  // ユーザー指定のオプションがあればマージして検証
  if (options) {
    // header の検証と正規化
    if (options.header !== undefined) {
      if (
        typeof options.header === 'number' ||
        options.header === 'A' ||
        (Array.isArray(options.header) &&
          options.header.every((h) => typeof h === 'string'))
      ) {
        normalizedOptions.header = options.header
      } else {
        console.warn(
          `[SheetJS] Invalid 'header' option: ${options.header}. Using default value: ${defaultOptions.header}`,
        )
        // 不正な値の場合はデフォルト値に戻す
        normalizedOptions.header = defaultOptions.header
      }
    }

    // range の検証と正規化
    if (options.range !== undefined) {
      if (validateExcelRangeValue(options.range)) {
        normalizedOptions.range = /^\d+$/.test(`${options.range}`)
          ? Number(options.range)
          : options.range
      } else {
        console.warn(
          `[SheetJS] Invalid 'range' option: ${options.range}. Using default value: ${defaultOptions.range}`,
        )
        normalizedOptions.range = defaultOptions.range
      }
    }

    // blankrows の検証と正規化
    if (options.blankrows !== undefined) {
      if (typeof options.blankrows === 'boolean') {
        normalizedOptions.blankrows = options.blankrows
      } else {
        console.warn(
          `[SheetJS] Invalid 'blankrows' option: ${options.blankrows}. Using default value: ${defaultOptions.blankrows}`,
        )
        normalizedOptions.blankrows = defaultOptions.blankrows
      }
    }

    // defval は任意の型を受け入れるため、特別な検証は不要（ただし、意図しない値が入らないように注意）
    if (options.defval !== undefined) {
      normalizedOptions.defval = options.defval
    }

    // raw の検証と正規化
    if (options.raw !== undefined) {
      if (typeof options.raw === 'boolean') {
        normalizedOptions.raw = options.raw
      } else {
        console.warn(
          `[SheetJS] Invalid 'raw' option: ${options.raw}. Using default value: ${defaultOptions.raw}`,
        )
        normalizedOptions.raw = defaultOptions.raw
      }
    }

    // rawNumbers の検証と正規化
    if (options.rawNumbers !== undefined) {
      if (typeof options.rawNumbers === 'boolean') {
        normalizedOptions.rawNumbers = options.rawNumbers
      } else {
        console.warn(
          `[SheetJS] Invalid 'rawNumbers' option: ${options.rawNumbers}. Using default value based on 'raw'.`,
        )
        // 不正な値の場合は、raw の値に基づいて決定（下の処理で実施）
        normalizedOptions.rawNumbers = undefined // 一旦リセット
      }
    }

    // dateNF などの DateNFOption からのオプションも同様に検証・正規化
    if (options.dateNF !== undefined) {
      if (typeof options.dateNF === 'string') {
        normalizedOptions.dateNF = options.dateNF
      } else {
        console.warn(
          `[SheetJS] Invalid 'dateNF' option: ${options.dateNF}. Using default value: ${defaultOptions.dateNF}`,
        )
        normalizedOptions.dateNF = defaultOptions.dateNF
      }
    }
    // 他の DateNFOption プロパティ (cellDates, sheetStubs など) も必要に応じて追加
  }

  return normalizedOptions
}
export type ExportAsExcel = typeof $exportAsExcel
